import { NoteEvent, Frame } from './types';

// ── Binary search: find index of nearest value in a sorted array ──
export function nearestIndex(arr: { t: number }[], target: number): number {
  if (arr.length === 0) return -1;
  let lo = 0, hi = arr.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t < target) lo = mid + 1;
    else hi = mid;
  }
  // lo is the first element >= target; check lo and lo-1
  if (lo > 0 && Math.abs(arr[lo - 1].t - target) <= Math.abs(arr[lo].t - target)) {
    return lo - 1;
  }
  return lo;
}

export function nearestTimeIndex(times: number[], target: number): number {
  if (times.length === 0) return -1;
  let lo = 0, hi = times.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(times[lo - 1] - target) <= Math.abs(times[lo] - target)) {
    return lo - 1;
  }
  return lo;
}

// ── Colors ─────────────────────────────────────────────────

// Volume color using harmonic band palette
// quiet = blue [96,165,250] → mid = purple [192,132,252] → loud = red [248,113,113]
// dbMin/dbMax from the recording's actual range
export function slopeColor(db: number | null, dbMin: number = -50, dbMax: number = -10): string {
  if (db == null) return 'rgba(107, 114, 128, 0.5)';

  const t = Math.max(0, Math.min(1, (db - dbMin) / Math.max(dbMax - dbMin, 1)));

  let r: number, g: number, b: number;
  if (t < 0.5) {
    // Quiet: blue → purple
    const s = t / 0.5;
    r = 96 + (192 - 96) * s;
    g = 165 + (132 - 165) * s;
    b = 250 + (252 - 250) * s;
  } else {
    // Loud: purple → red
    const s = (t - 0.5) / 0.5;
    r = 192 + (248 - 192) * s;
    g = 132 + (113 - 132) * s;
    b = 252 + (113 - 252) * s;
  }

  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, 0.85)`;
}

export function noteIntensity(note: NoteEvent, frames?: Frame[]): number | null {
  if (!frames || !note.frame_indices) return null;
  const vals: number[] = [];
  for (const i of note.frame_indices) {
    const f = frames[i];
    if (!f) continue;
    vals.push(f.intensity_db);
  }
  if (vals.length === 0) return null;
  vals.sort((a, b) => a - b);
  return vals[Math.floor(vals.length / 2)];
}

// Fallback for categorical shapes (backwards compat)
export const SHAPE_COLORS: Record<string, string> = {
  convergent: '#3b82f6',
  divergent: '#f59e0b',
  rectangular: '#8b5cf6',
  uncertain: '#6b7280',
};

// ── Helpers ────────────────────────────────────────────────

export function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 10);
  return `${m}:${sec.toString().padStart(2, '0')}.${ms}`;
}

export function fmt(val: number | null | undefined, decimals = 2, unit = ''): string {
  if (val === null || val === undefined) return '–';
  return val.toFixed(decimals) + unit;
}

export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Harmonic colors shared between HarmonicBars spectrum chart and Analysis tab
export const HARMONIC_COLORS: [number, number, number][] = [
  [96, 165, 250],   // blue
  [34, 211, 238],   // cyan
  [52, 211, 153],   // emerald
  [163, 230, 53],   // lime
  [250, 204, 21],   // yellow
  [251, 146, 60],   // orange
  [248, 113, 113],  // red
  [192, 132, 252],  // purple
  [244, 114, 182],  // pink
  [45, 212, 191],   // teal
];

export function harmonicColor(n: number, alpha = 1): string {
  const rgb = HARMONIC_COLORS[(n - 1) % HARMONIC_COLORS.length];
  return alpha < 1 ? `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})` : `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}
